import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import type { AuthClient } from "../authClient";
import { useNotification } from "../../ui";
import { validateEmail } from "../../utils";
import { AuthPage } from "./AuthPage";

export interface AuthForgotPasswordProps {
  appTitle: string;
  logo: string;
  authClient: AuthClient;
}

export function ForgotPassword({
  appTitle,
  logo,
  authClient,
}: AuthForgotPasswordProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [sendingPasswordEmail, setSendingPasswordEmail] = useState(false);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});

  const handleEmailChange = (value: string) => {
    setEmail(value);
    const error = value && !validateEmail(value) ? t("EmailInvalid") : "";
    setValidationErrors((prev) => ({ ...prev, email: error }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!email || !validateEmail(email)) {
      setValidationErrors({ email: t("EmailInvalid") });
      return;
    }

    setSendingPasswordEmail(true);

    try {
      await authClient.forgetPassword(email);
      showSuccess(t("PasswordResetEmailSent"));
      setSuccess(t("PasswordResetEmailSent"));
      setTimeout(() => {
        navigate("/login");
      }, 3000);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Fehler beim Senden der E-Mail";
      showError(errorMessage);
      setError(errorMessage);
    } finally {
      setSendingPasswordEmail(false);
    }
  };

  return (
    <AuthPage appTitle={appTitle} logo={logo}>
      <Typography
        component="h2"
        variant="body1"
        sx={{ mb: 1, color: "text.secondary" }}
      >
        {t("ForgotPassword")}
      </Typography>
      <Typography
        variant="body2"
        sx={{ mb: 4, color: "text.secondary", textAlign: "center" }}
      >
        {t("PasswordChangeInstructions")}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ width: "100%", mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          sx={{ width: "100%", mb: 3, borderRadius: 2 }}
        >
          {success}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit} sx={{ width: "100%" }}>
        <TextField
          margin="normal"
          required
          fullWidth
          id="email"
          label={t("Email")}
          name="email"
          type="email"
          autoComplete="email"
          autoFocus
          value={email}
          onChange={(e) => handleEmailChange(e.target.value)}
          error={!!validationErrors.email}
          helperText={validationErrors.email}
          sx={{ mb: 3 }}
        />
        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          disabled={sendingPasswordEmail || !email || !validateEmail(email)}
          startIcon={
            sendingPasswordEmail ? <CircularProgress size={20} /> : null
          }
          sx={{ py: 1.5, fontSize: "1rem", fontWeight: 600, mb: 2 }}
        >
          {sendingPasswordEmail ? t("Loading...") : t("SendResetEmail")}
        </Button>
        <Box sx={{ textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            {t("RememberPassword")}{" "}
            <Link
              to="/login"
              style={{
                color: "inherit",
                textDecoration: "none",
                fontWeight: 600,
              }}
            >
              {t("Login")}
            </Link>
          </Typography>
        </Box>
      </Box>
    </AuthPage>
  );
}
